import { AnalyticsEngineCtrlApi } from "./generated/api";
import type { BuildLog, EngineHealth, EngineStatus, ModelConfig, PodLogs, RabbitQueueInfo, ScaleRequest, WorkerStatus } from "./generated/api";
import { apiCall, config, getConfig, serverUrl, axiosInstance } from "./Utils";
import type { ApiResult } from "../types/api";

const engineApi = new AnalyticsEngineCtrlApi(config, serverUrl, axiosInstance);

// ── Santé des moteurs ────────────────────────────────────────────────────────

/**
 * Vérifie la santé de tous les moteurs analytiques.
 * Endpoint : GET /api/analytics/engines/health
 */
async function checkAllHealth(): Promise<ApiResult<EngineHealth[]>> {
    return apiCall(engineApi.apiAnalyticsEnginesHealthGet(getConfig()));
}

/**
 * Vérifie la santé d'un moteur analytique spécifique.
 * Endpoint : GET /api/analytics/engines/health/{modelId}
 */
async function checkHealth(modelId: string): Promise<ApiResult<EngineHealth>> {
    return apiCall(engineApi.apiAnalyticsEnginesHealthModelIdGet(modelId, getConfig()));
}

// ── Kubernetes ───────────────────────────────────────────────────────────────

/**
 * Récupère le statut de tous les déploiements K8s.
 * Endpoint : GET /api/analytics/engines/k8s/status
 */
async function getAllEngineStatuses(): Promise<ApiResult<EngineStatus[]>> {
    return apiCall(engineApi.apiAnalyticsEnginesK8sStatusGet(getConfig()));
}

/**
 * Récupère le statut d'un déploiement K8s spécifique.
 * Endpoint : GET /api/analytics/engines/k8s/{modelId}/status
 */
async function getEngineStatus(modelId: string): Promise<ApiResult<EngineStatus>> {
    return apiCall(engineApi.apiAnalyticsEnginesK8sModelIdStatusGet(modelId, getConfig()));
}

/**
 * Déploie un moteur analytique sur le cluster K8s.
 * Endpoint : POST /api/analytics/engines/k8s/{modelId}/deploy
 */
async function deployEngine(modelId: string): Promise<ApiResult<void>> {
    return apiCall(engineApi.apiAnalyticsEnginesK8sModelIdDeployPost(modelId, getConfig()));
}

/**
 * Supprime le déploiement K8s d'un moteur analytique.
 * Endpoint : DELETE /api/analytics/engines/k8s/{modelId}
 */
async function undeployEngine(modelId: string): Promise<ApiResult<void>> {
    return apiCall(engineApi.apiAnalyticsEnginesK8sModelIdDelete(modelId, getConfig()));
}

/**
 * Ajuste le nombre de réplicas d'un moteur K8s.
 * Endpoint : PUT /api/analytics/engines/k8s/{modelId}/scale
 */
async function scaleEngine(modelId: string, replicas: number): Promise<ApiResult<void>> {
    const req: ScaleRequest = { replicas };
    return apiCall(engineApi.apiAnalyticsEnginesK8sModelIdScalePut(modelId, req, getConfig()));
}

/**
 * Relance un déploiement K8s (rolling restart).
 * Endpoint : GET /api/analytics/engines/k8s/{modelId}/rollout
 */
async function rolloutEngine(modelId: string): Promise<ApiResult<void>> {
    return apiCall(axiosInstance.get(`${serverUrl}/api/analytics/engines/k8s/${encodeURIComponent(modelId)}/rollout`, getConfig()));
}

// ── Modèles analytiques ──────────────────────────────────────────────────────

/**
 * Récupère la liste plate de tous les modèles enregistrés (nouveau format).
 * Endpoint : GET /api/analytics/engines/models
 */
async function getEngineModels(): Promise<ApiResult<ModelConfig[]>> {
    return apiCall(engineApi.apiAnalyticsEnginesModelsGet(getConfig()));
}

/**
 * Récupère un modèle par son identifiant.
 * Endpoint : GET /api/analytics/engines/models/{id}
 */
async function getEngineModel(id: string): Promise<ApiResult<ModelConfig>> {
    return apiCall(engineApi.apiAnalyticsEnginesModelsIdGet(id, getConfig()));
}

/**
 * Crée ou met à jour un modèle analytique (format plat).
 * Endpoint : POST /api/analytics/engines/models
 */
async function saveEngineModel(model: ModelConfig): Promise<ApiResult<ModelConfig>> {
    return apiCall(engineApi.apiAnalyticsEnginesModelsPost(model, getConfig()));
}

/**
 * Supprime un modèle analytique.
 * Endpoint : DELETE /api/analytics/engines/models/{id}
 */
async function deleteEngineModel(id: string): Promise<ApiResult<void>> {
    return apiCall(engineApi.apiAnalyticsEnginesModelsIdDelete(id, getConfig()));
}

/**
 * Récupère les fichiers plateforme (connector, etc.) pour un langage.
 * Endpoint : GET /api/analytics/engines/platform/{language}
 */
async function getPlatformFiles(language: string): Promise<ApiResult<Record<string, string>>> {
    return apiCall(engineApi.apiAnalyticsEnginesPlatformLanguageGet(language, getConfig()));
}

/**
 * Importe un modèle analytique depuis un fichier ZIP.
 * Endpoint : POST /api/analytics/engines/models/upload
 */
async function uploadEngineModel(file: File): Promise<ApiResult<ModelConfig>> {
    return apiCall(engineApi.apiAnalyticsEnginesModelsUploadPost(file, getConfig()));
}

// ── RabbitMQ ─────────────────────────────────────────────────────────────────

/**
 * Récupère la liste des queues RabbitMQ actives.
 * Endpoint : GET /api/analytics/engines/rabbitmq/queues
 */
async function getRabbitQueues(): Promise<ApiResult<RabbitQueueInfo[]>> {
    return apiCall(engineApi.apiAnalyticsEnginesRabbitmqQueuesGet(getConfig()));
}

/**
 * Récupère le statut de tous les workers RabbitMQ.
 * Endpoint : GET /api/analytics/engines/rabbitmq/workers
 */
async function getAllWorkerStatuses(): Promise<ApiResult<WorkerStatus[]>> {
    return apiCall(engineApi.apiAnalyticsEnginesRabbitmqWorkersGet(getConfig()));
}

/**
 * Récupère le statut des workers d'un moteur (consommateurs RabbitMQ).
 * Endpoint : GET /api/analytics/engines/rabbitmq/workers/{engineId}
 */
async function getWorkerStatus(engineId: string): Promise<ApiResult<WorkerStatus>> {
    return apiCall(engineApi.apiAnalyticsEnginesRabbitmqWorkersEngineIdGet(engineId, getConfig()));
}

/**
 * Purge tous les messages d'une queue RabbitMQ.
 * Endpoint : DELETE /api/analytics/engines/rabbitmq/queues/{name}/messages
 */
async function purgeQueue(name: string): Promise<ApiResult<void>> {
    return apiCall(engineApi.apiAnalyticsEnginesRabbitmqQueuesNameMessagesDelete(name, getConfig()));
}

/**
 * Ferme une connexion RabbitMQ par son nom.
 * Endpoint : DELETE /api/analytics/engines/rabbitmq/connections/{name}
 */
async function disconnectRabbitConnection(name: string): Promise<ApiResult<void>> {
    return apiCall(engineApi.apiAnalyticsEnginesRabbitmqConnectionsNameDelete(name, getConfig()));
}

/**
 * Lit (sans consommer) N messages d'une queue RabbitMQ pour diagnostique.
 * Endpoint : GET /api/analytics/engines/rabbitmq/queues/{name}/peek?count=N
 */
async function peekQueueMessages(name: string, count: number = 1): Promise<ApiResult<unknown[]>> {
    return apiCall(engineApi.apiAnalyticsEnginesRabbitmqQueuesNamePeekGet(name, count, getConfig()));
}

// ── Logs ─────────────────────────────────────────────────────────────────────

/**
 * Récupère les logs d'un moteur analytique.
 * Endpoint : GET /api/analytics/engines/logs/{modelId}
 */
async function getEngineLogs(modelId: string, tailLines?: number): Promise<ApiResult<PodLogs>> {
    return apiCall(engineApi.apiAnalyticsEnginesLogsModelIdGet(modelId, tailLines, getConfig()));
}

// ── Build logs ────────────────────────────────────────────────────────────────

/**
 * Récupère tous les logs de build d'un moteur analytique.
 * Endpoint : GET /api/analytics/engines/builds/{modelId}
 */
async function getBuildLogs(modelId: string): Promise<ApiResult<BuildLog[]>> {
    return apiCall(engineApi.apiAnalyticsEnginesBuildsModelIdGet(modelId, getConfig()));
}

/**
 * Récupère le dernier log de build d'un moteur analytique.
 * Endpoint : GET /api/analytics/engines/builds/{modelId}/latest
 */
async function getLatestBuildLog(modelId: string): Promise<ApiResult<BuildLog>> {
    return apiCall(engineApi.apiAnalyticsEnginesBuildsModelIdLatestGet(modelId, getConfig()));
}

/**
 * Récupère les logs de build en direct (texte brut) d'un moteur analytique.
 * Endpoint : GET /api/analytics/engines/builds/{modelId}/live
 */
async function getLiveBuildLogs(modelId: string): Promise<ApiResult<string>> {
    return apiCall(engineApi.apiAnalyticsEnginesBuildsModelIdLiveGet(modelId, getConfig()));
}

/**
 * Récupère le statut du build d'un moteur analytique.
 * Endpoint : GET /api/analytics/engines/builds/{modelId}/status
 */
async function getBuildStatus(modelId: string): Promise<ApiResult<unknown>> {
    return apiCall(engineApi.apiAnalyticsEnginesBuildsModelIdStatusGet(modelId, getConfig()));
}

/**
 * Récupère un log de build par son identifiant.
 * Endpoint : GET /api/analytics/engines/builds/detail/{buildId}
 */
async function getBuildLog(buildId: string): Promise<ApiResult<BuildLog>> {
    return apiCall(engineApi.apiAnalyticsEnginesBuildsDetailBuildIdGet(buildId, getConfig()));
}

/**
 * Récupère le texte brut d'un log de build par son identifiant.
 * Endpoint : GET /api/analytics/engines/builds/detail/{buildId}/logs
 */
async function getBuildLogText(buildId: string): Promise<ApiResult<string>> {
    return apiCall(engineApi.apiAnalyticsEnginesBuildsDetailBuildIdLogsGet(buildId, getConfig()));
}

const ApiAnalyticsEngineCtrl = {
    // Santé
    checkAllHealth,
    checkHealth,
    // Kubernetes
    getAllEngineStatuses,
    getEngineStatus,
    deployEngine,
    undeployEngine,
    scaleEngine,
    rolloutEngine,
    // Modèles
    getEngineModels,
    getEngineModel,
    saveEngineModel,
    deleteEngineModel,
    uploadEngineModel,
    getPlatformFiles,
    // RabbitMQ
    getRabbitQueues,
    getAllWorkerStatuses,
    getWorkerStatus,
    purgeQueue,
    disconnectRabbitConnection,
    peekQueueMessages,
    // Logs
    getEngineLogs,
    // Build logs
    getBuildLogs,
    getLatestBuildLog,
    getLiveBuildLogs,
    getBuildStatus,
    getBuildLog,
    getBuildLogText,
};

export default ApiAnalyticsEngineCtrl;

